import { createServerFn } from '@tanstack/react-start';
import chunk from 'lodash/chunk';
import uniq from 'lodash/uniq';
import z from 'zod';

import { MAX_OBJECT_IDS_PER_REQUEST } from './utils';
import { abapLintIssueSearch, fleIssueSearch } from './utils.function';

import { sourceCodeV3QueryOptions } from '@/api/endpoints/remedy';
import { sessionMiddleware } from '@/middleware/session';
import { getQueryClient } from '@/query';
import type { TLAICodeFix, TLAINextFindObjectsWithIssuesRequest, TLAINextObjectIssue } from '@/types/api';
import { withRetry } from '@/utils/helpers';
import { extractHTTPError } from '@/utils/http-client';

export type StaticDiagnostic = {
  issue_key: string;
  fixes: TLAICodeFix[];
};

export type FleDiagnostic = {
  issue_key: string;
  line_num: number;
  after: string;
};

async function findObjectIssues(remedy_id: string, object_id: string): Promise<TLAINextObjectIssue> {
  const queryClient = getQueryClient();

  const { data, error } = await withRetry(
    () => queryClient.fetchQuery(sourceCodeV3QueryOptions({ remedy_id, object_id })),
    {
      retries: 2,
      delay: 500,
      shouldRetryOnSuccess: (result) => !result?.program?.code,
    },
  );

  if (error) {
    return {
      object_id,
      errors: [extractHTTPError(error, 'An unknown error occurred while fetching source code')],
      issue_keys: [],
    };
  }

  if (!data.program.code) {
    return {
      object_id,
      errors: ['Source code not found'],
      issue_keys: [],
    };
  }

  const { staticDiagnostics, fleDiagnostics, errors } = await searchForIssues({
    data: {
      remedy_id,
      object_id,
      code: data.program.code,
    },
  });

  const issue_keys = uniq([
    ...staticDiagnostics.map((diagnostic) => diagnostic.issue_key),
    ...fleDiagnostics.map((diagnostic) => diagnostic.issue_key),
  ]);

  return { object_id, issue_keys, errors };
}

export const findObjectsWithIssuesStream = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      remedy_id: z.string(),
      object_ids: z.array(z.string()),
      max_object_ids_per_request: z.number().optional().default(MAX_OBJECT_IDS_PER_REQUEST),
    }) satisfies z.ZodType<TLAINextFindObjectsWithIssuesRequest>,
  )
  .handler(async function* ({
    data: { remedy_id, object_ids, max_object_ids_per_request },
  }): AsyncGenerator<TLAINextObjectIssue[], void, undefined> {
    if (object_ids.length === 0) return;

    const objectChunks = chunk(object_ids, max_object_ids_per_request);

    console.info(
      `[FIX SEARCH][START] remedy_id=${remedy_id} total_objects=${object_ids.length} chunk_size=${max_object_ids_per_request} chunk_count=${objectChunks.length}`,
    );

    for (let i = 0; i < objectChunks.length; i++) {
      const objectChunk = objectChunks[i];

      console.info(
        `[FIX SEARCH][CHUNK][START] remedy_id=${remedy_id} chunk=${i + 1}/${objectChunks.length} objects_in_chunk=${objectChunk.length}`,
      );

      const chunkResults = await Promise.all(objectChunk.map((object_id) => findObjectIssues(remedy_id, object_id)));

      yield chunkResults;
    }
  });

export const searchForIssues = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(
    z.object({
      remedy_id: z.string(),
      object_id: z.string(),
      code: z.string(),
    }),
  )
  .handler(async ({ data: { remedy_id, object_id, code }, context: { session } }) => {
    const [
      { diagnostics: staticDiagnostics, error: staticDiagnosticsError },
      { diagnostics: fleDiagnostics, error: fleDiagnosticsError },
    ] = await Promise.all([
      abapLintIssueSearch({ object_id, code }),
      fleIssueSearch({ remedy_id, object_id, code, session }),
    ]);

    const errors: string[] = [];
    if (staticDiagnosticsError) {
      errors.push(
        extractHTTPError(staticDiagnosticsError, 'An unknown error occurred while searching for ABAP issues'),
      );
    }

    if (fleDiagnosticsError) {
      errors.push(extractHTTPError(fleDiagnosticsError, 'An unknown error occurred while searching for FLE issues'));
    }

    return {
      staticDiagnostics,
      fleDiagnostics,
      errors: errors.length > 0 ? errors : undefined,
    };
  });
